import { useState, useRef } from "react";
import axios from "axios";
import { CheckCircle2 } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { Reveal } from "../components/site/Reveal";
import { PrimaryButton } from "../components/site/Buttons";
import { API } from "../lib/site";
import { Seo } from "../components/site/Seo";
import { trackEvent } from "../lib/analytics";
import { toast } from "sonner";

const INDUSTRIES = [
  "Real Estate", "Finance & Investments", "Technology", "Healthcare", "E-Commerce & Retail",
  "Construction & Trades", "Marketing & Media", "Legal & Professional Services",
  "Transportation & Logistics", "Hospitality & Food", "Education & Training",
  "Entertainment & Sports", "Manufacturing", "Other",
];

const initial = {
  first_name: "", last_name: "", email: "", phone: "", city: "", state: "",
  occupation: "", company_name: "", primary_industry: "", business_owner: "",
  investor: "", years_in_business: "", how_heard: "", referred_by: "",
  hoping_to_gain: "", contribution: "", why_consider: "", linkedin_url: "",
  website_url: "", agree_terms: false, confirm_company: "",
};

const Field = ({ label, required, children }) => (
  <div>
    <label className="mst-label">{label}{required && " *"}</label>
    {children}
  </div>
);

const YesNo = ({ value, onChange, testId }) => (
  <div className="flex gap-3">
    {["Yes", "No"].map((opt) => (
      <button
        key={opt}
        type="button"
        data-testid={`${testId}-${opt.toLowerCase()}`}
        onClick={() => onChange(opt)}
        className={`px-8 py-3 rounded-xl text-sm font-bold border transition-colors duration-200 ${
          value === opt
            ? "bg-gradient-to-r from-[#006FD6] to-[#009BFF] text-white border-transparent shadow-[0_4px_14px_rgba(0,111,214,0.35)]"
            : "bg-white text-[#041525] border-[#062541]/20 hover:border-[#006FD6]"
        }`}
        style={{ fontFamily: "var(--font-heading)" }}
      >
        {opt}
      </button>
    ))}
  </div>
);

export default function Apply() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const started = useRef(false);

  const set = (k) => (e) => {
    if (!started.current && k !== "confirm_company") {
      started.current = true;
      trackEvent("membership_application_start");
    }
    setForm({ ...form, [k]: e.target.value });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_owner || !form.investor) {
      toast.error("Please answer the Business Owner and Investor questions.");
      return;
    }
    if (!form.agree_terms) {
      toast.error("Please acknowledge the membership terms checkbox.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, {
        ...form,
        business_owner: form.business_owner === "Yes",
        investor: form.investor === "Yes",
      });
      setDone(true);
      trackEvent("membership_application_submit");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Something went wrong submitting your application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main data-testid="apply-page">
        <section className="relative bg-[#020A12] min-h-[85vh] flex items-center overflow-hidden">
          <div className="absolute inset-0 mst-dot-pattern opacity-20" />
          <div className="relative max-w-2xl mx-auto px-6 text-center py-40">
            <Reveal>
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-[#006FD6] to-[#009BFF] flex items-center justify-center shadow-[0_10px_40px_rgba(0,155,255,0.4)]">
                <CheckCircle2 size={38} strokeWidth={1.5} className="text-white" />
              </div>
              <h1 data-testid="apply-success-title" className="mt-10 text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
                Application Received
              </h1>
              <p className="mt-6 text-white/60 leading-relaxed text-lg">
                Thank you for your interest in MST. Our membership team will review your
                application and contact you regarding next steps. Applications are typically
                reviewed within 3&ndash;5 business days.
              </p>
              <div className="mt-10">
                <PrimaryButton to="/" data-testid="apply-success-home-btn">Back to Home</PrimaryButton>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main data-testid="apply-page">
      <Seo
        title="Apply for Membership | MST — Multiple Stream Team"
        description="Apply to join MST — a private, invite-only network of entrepreneurs, investors, founders, and leaders. Every application is reviewed for fit, alignment, and eligibility."
        path="/apply"
      />
      <PageHero
        label="Membership Application"
        title={<>Apply for <span className="text-[#009BFF]">MST Membership</span></>}
        text="MST is a private, invite-only organization. Every application is reviewed by the MST membership team for fit, alignment, and eligibility."
      />
      <section className="bg-[#F6F8FA] py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <Reveal>
            <form onSubmit={submit} data-testid="apply-form" className="rounded-2xl bg-white border border-[#062541]/10 shadow-[0_20px_60px_rgba(4,21,37,0.08)] p-8 sm:p-12 space-y-10">
              <input
                type="text"
                name="confirm_company"
                value={form.confirm_company}
                onChange={set("confirm_company")}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="absolute -left-[9999px] top-auto h-px w-px opacity-0"
              />
              <div>
                <h2 className="text-xl font-extrabold text-[#041525]" style={{ fontFamily: "var(--font-heading)" }}>Personal Information</h2>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <Field label="First Name" required><input data-testid="apply-first-name" required className="mst-input" value={form.first_name} onChange={set("first_name")} /></Field>
                  <Field label="Last Name" required><input data-testid="apply-last-name" required className="mst-input" value={form.last_name} onChange={set("last_name")} /></Field>
                  <Field label="Email" required><input data-testid="apply-email" required type="email" className="mst-input" value={form.email} onChange={set("email")} /></Field>
                  <Field label="Phone" required><input data-testid="apply-phone" required type="tel" className="mst-input" value={form.phone} onChange={set("phone")} /></Field>
                  <Field label="City" required><input data-testid="apply-city" required className="mst-input" value={form.city} onChange={set("city")} /></Field>
                  <Field label="State" required><input data-testid="apply-state" required className="mst-input" value={form.state} onChange={set("state")} /></Field>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-extrabold text-[#041525]" style={{ fontFamily: "var(--font-heading)" }}>Professional Background</h2>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <Field label="Occupation / Profession" required><input data-testid="apply-occupation" required className="mst-input" value={form.occupation} onChange={set("occupation")} /></Field>
                  <Field label="Company Name"><input data-testid="apply-company" className="mst-input" value={form.company_name} onChange={set("company_name")} /></Field>
                  <Field label="Primary Industry" required>
                    <select data-testid="apply-industry" required className="mst-input" value={form.primary_industry} onChange={set("primary_industry")}>
                      <option value="">Select an industry</option>
                      {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                    </select>
                  </Field>
                  <Field label="Years in Business"><input data-testid="apply-years" className="mst-input" value={form.years_in_business} onChange={set("years_in_business")} /></Field>
                  <Field label="Business Owner?" required><YesNo testId="apply-business-owner" value={form.business_owner} onChange={(v) => setForm({ ...form, business_owner: v })} /></Field>
                  <Field label="Investor?" required><YesNo testId="apply-investor" value={form.investor} onChange={(v) => setForm({ ...form, investor: v })} /></Field>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-extrabold text-[#041525]" style={{ fontFamily: "var(--font-heading)" }}>About Your Interest in MST</h2>
                <div className="mt-6 space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <Field label="How did you hear about MST?"><input data-testid="apply-how-heard" className="mst-input" value={form.how_heard} onChange={set("how_heard")} /></Field>
                    <Field label="Who referred you?"><input data-testid="apply-referred-by" className="mst-input" value={form.referred_by} onChange={set("referred_by")} /></Field>
                  </div>
                  <Field label="What are you hoping to gain from MST?" required>
                    <textarea data-testid="apply-hoping-to-gain" required rows={4} className="mst-input resize-none" value={form.hoping_to_gain} onChange={set("hoping_to_gain")} />
                  </Field>
                  <Field label="What skills, experience, resources, relationships, or opportunities can you contribute to MST?" required>
                    <textarea data-testid="apply-contribution" required rows={4} className="mst-input resize-none" value={form.contribution} onChange={set("contribution")} />
                  </Field>
                  <Field label="Why should MST consider you for membership?" required>
                    <textarea data-testid="apply-why-consider" required rows={4} className="mst-input resize-none" value={form.why_consider} onChange={set("why_consider")} />
                  </Field>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <Field label="LinkedIn URL"><input data-testid="apply-linkedin" className="mst-input" value={form.linkedin_url} onChange={set("linkedin_url")} placeholder="https://linkedin.com/in/..." /></Field>
                    <Field label="Website URL"><input data-testid="apply-website" className="mst-input" value={form.website_url} onChange={set("website_url")} placeholder="https://..." /></Field>
                  </div>
                </div>
              </div>

              <label className="flex items-start gap-4 cursor-pointer rounded-xl border border-[#062541]/15 bg-[#F6F8FA] p-5">
                <input
                  data-testid="apply-agree-checkbox"
                  type="checkbox"
                  checked={form.agree_terms}
                  onChange={(e) => setForm({ ...form, agree_terms: e.target.checked })}
                  className="mt-0.5 w-5 h-5 accent-[#006FD6] shrink-0"
                />
                <span className="text-sm text-[#041525] leading-relaxed">
                  I understand MST is a private, invite-only organization and submitting an
                  application does not guarantee membership.
                </span>
              </label>

              <PrimaryButton data-testid="apply-submit-btn" type="submit" disabled={submitting} className="w-full !py-4 disabled:opacity-60">
                {submitting ? "Submitting..." : "Submit Application"}
              </PrimaryButton>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
